import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Alert,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AlertTitle
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import CategoryIcon from "@mui/icons-material/Category";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import client from "../../../API";
import MuiPhoneInput from "../../../Components/MuiPhoneInput";
import { useQueryClient } from "react-query";
import { niches, parseLocalFloat, parseLocalInt } from "../../../Utils/constants";
import { toast } from 'sonner';

const formatNumber = (value) => {
  const number = parseInt(value, 10);
  return isNaN(number) ? "0" : number.toString();
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`creator-tabpanel-${index}`}
      aria-labelledby={`creator-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Platform configurations with validation rules
const PLATFORM_CONFIGS = {
  TikTok: { 
    emoji: '🎵', 
    color: '#000000', 
    prefix: 'tiktok.com/@',
    requiresAt: false,
    urlBase: 'https://www.tiktok.com/@'
  },
  Instagram: { 
    emoji: '📸', 
    color: '#E4405F', 
    prefix: 'instagram.com/',
    requiresAt: false,
    urlBase: 'https://www.instagram.com/'
  },
  YouTube: { 
    emoji: '▶️', 
    color: '#FF0000', 
    prefix: 'youtube.com/',
    requiresAt: true,
    urlBase: 'https://www.youtube.com/'
  },
  X: { 
    emoji: '𝕏', 
    color: '#000000', 
    prefix: 'x.com/',
    requiresAt: false,
    urlBase: 'https://www.x.com/'
  },
  Podcast: { 
    emoji: '🎙️', 
    color: '#9C27B0', 
    prefix: '',
    isUrl: true,
    requiresAt: false
  },
  Kick: { 
    emoji: '🎮', 
    color: '#53FC18', 
    prefix: 'kick.com/',
    requiresAt: false,
    urlBase: 'https://www.kick.com/'
  },
  Twitch: { 
    emoji: '🟣', 
    color: '#9146FF', 
    prefix: 'twitch.tv/',
    requiresAt: false,
    urlBase: 'https://www.twitch.tv/'
  },
};

// Sanitize username input
const sanitizeUsername = (value, platform) => {
  let sanitized = value;
  const config = PLATFORM_CONFIGS[platform];
  
  if (config?.isUrl) {
    // For URLs (like Podcast), just trim whitespace
    return sanitized.trim();
  }
  
  if (platform === 'YouTube') {
    // YouTube REQUIRES @ symbol at the start
    if (!sanitized.startsWith('@')) {
      sanitized = '@' + sanitized;
    }
    // Remove any @ symbols that aren't at the start
    sanitized = '@' + sanitized.slice(1).replace(/@/g, '');
    // Allow only valid characters after the @
    sanitized = sanitized[0] + sanitized.slice(1).replace(/[^a-zA-Z0-9_.-]/g, '');
  } else {
    // Remove leading @ symbol for other platforms
    sanitized = sanitized.replace(/^@+/, '');
    // Allow only alphanumeric, underscore, and period
    sanitized = sanitized.replace(/[^a-zA-Z0-9_.]/g, '');
  }
  
  return sanitized;
};

// Validate username format
const validateUsername = (value, platform) => {
  if (!value || value.trim() === '') {
    return { valid: false, message: 'This field is required' };
  }
  
  const config = PLATFORM_CONFIGS[platform];
  
  // For URLs (like Podcast), validate URL format
  if (config?.isUrl) {
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (!urlPattern.test(value)) {
      return { valid: false, message: 'Please enter a valid URL' };
    }
    return { valid: true, message: 'Valid URL' };
  }
  
  // Special validation for YouTube
  if (platform === 'YouTube') {
    if (!value.startsWith('@')) {
      return { valid: false, message: 'YouTube handle must start with @' };
    }
    
    const usernameAfterAt = value.slice(1);
    
    if (usernameAfterAt.length < 1) {
      return { valid: false, message: 'YouTube handle is too short (minimum 1 character after @)' };
    }
    
    if (usernameAfterAt.length > 30) {
      return { valid: false, message: 'YouTube handle is too long (max 30 characters after @)' };
    }
    
    if (!/^[a-zA-Z0-9_.-]+$/.test(usernameAfterAt)) {
      return { 
        valid: false, 
        message: 'YouTube handle can only contain letters, numbers, underscores, periods, and hyphens after @' 
      };
    }
    
    return { valid: true, message: 'Valid YouTube handle' };
  }
  
  // Validation for other platforms
  const sanitized = sanitizeUsername(value, platform);
  
  if (sanitized !== value) {
    return { 
      valid: false, 
      message: 'Username contains invalid characters. Only letters, numbers, underscores, and periods are allowed.' 
    };
  }
  
  if (sanitized.length < 1) {
    return { valid: false, message: 'Username is too short.' };
  }
  
  if (sanitized.length > 30) {
    return { valid: false, message: 'Username is too long (max 30 characters).' };
  }
  
  return { valid: true, message: 'Valid username' };
};

const CreatorDialog = ({ open, onClose, creatorInfo, mode }) => {
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState(0);
  const [savingSection, setSavingSection] = useState(null);
  const [expandedPlatform, setExpandedPlatform] = useState(false);
  const [platformValidation, setPlatformValidation] = useState({});

  // Basic Information State
  const [creatorName, setCreatorName] = useState(creatorInfo?.display_name || "");
  const [creatorId, setCreatorId] = useState(creatorInfo?.creator || "");
  const [creatorRegion, setCreatorRegion] = useState(creatorInfo?.region || "");
  const [tierLevel, setTierLevel] = useState(creatorInfo?.tier_level || "");
  const [promotionalBody, setPromotionalBody] = useState(creatorInfo?.promotional_body || "");

  // Contact Information State
  const [contactEmail, setContactEmail] = useState(creatorInfo?.email || "");
  const [paymentEmail, setPaymentEmail] = useState(creatorInfo?.payout_email || creatorInfo?.email || "");
  const [phoneNumber, setPhoneNumber] = useState(creatorInfo?.phone_number || "");
  const [emailSame, setEmailSame] = useState(false);

  // Demographics State
  const [race, setRace] = useState(creatorInfo?.ethnicity || "");
  const [gender, setGender] = useState(creatorInfo?.gender || "");
  const [location, setLocation] = useState(creatorInfo?.geolocation || "");
  const [primaryMarket, setPrimaryMarket] = useState(creatorInfo?.primary_markets || []);
  const [notesContentStyle, setNotesContentStyle] = useState(creatorInfo?.notes_content_style || "");
  const [invalid_geogenderrace, setInvalid_geogenderrace] = useState(creatorInfo?.invalid_geogenderrace || false);

  // Additional Information State
  const [avgViews, setAvgViews] = useState(creatorInfo?.avg_views || "");
  const [manager, setManager] = useState(creatorInfo?.manager || "");
  const [supervisor, setSupervisor] = useState(creatorInfo?.superviser || "");
  const [pfphref, setPfphref] = useState(creatorInfo?.pfphref || "");
  const [isVendor, setIsVendor] = useState(creatorInfo?.is_vendor || false);
  const [marketplacePermission, setMarketplacePermission] = useState(
    creatorInfo?.marketplace_permission !== undefined ? creatorInfo.marketplace_permission : true
  );

  // Instagram states
  const [instagramLink, setInstagramLink] = useState("");
  const [instagramFollowerCount, setInstagramFollowerCount] = useState("");
  const [instagramBrandRate, setInstagramBrandRate] = useState("");
  const [instagramSongRate, setInstagramSongRate] = useState("");
  const [instagramFeedRate, setInstagramFeedRate] = useState("");
  const [instagramStoryRate, setInstagramStoryRate] = useState("");

  // TikTok states
  const [tikTokLink, setTikTokLink] = useState("");
  const [tikTokFollowerCount, setTikTokFollowerCount] = useState("");
  const [tikTokBrandRate, setTikTokBrandRate] = useState("");
  const [tikTokSongRate, setTikTokSongRate] = useState("");

  // YouTube states
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeFollowerCount, setYoutubeFollowerCount] = useState("");
  const [youtubeIntegrationRate, setYoutubeIntegrationRate] = useState("");
  const [youtubeShortsRate, setYoutubeShortsRate] = useState("");
  const [youtubeDedicatedRate, setYoutubeDedicatedRate] = useState("");

  // X (Twitter) states
  const [xLink, setXLink] = useState('');
  const [xFollowerCount, setXFollowerCount] = useState('');
  const [xFeedPost, setXFeedPost] = useState('');
  const [xRepost, setXRepost] = useState('');

  // Podcast states
  const [podcastLink, setPodcastLink] = useState('');
  const [podcastFollowerCount, setPodcastFollowerCount] = useState('');
  const [podcastPreroll, setPodcastPreroll] = useState('');
  const [podcastMidroll, setPodcastMidroll] = useState('');
  const [podcastPostroll, setPodcastPostroll] = useState('');
  const [podcastFull, setPodcastFull] = useState('');

  // Kick and Twitch states
  const [kickLink, setKickLink] = useState('');
  const [kickFollowerCount, setKickFollowerCount] = useState('');
  const [kickPlacement, setKickPlacement] = useState('');
  const [twitchLink, setTwitchLink] = useState('');
  const [twitchFollowerCount, setTwitchFollowerCount] = useState('');
  const [twitchPlacement, setTwitchPlacement] = useState('');

  useEffect(() => {
    if (creatorInfo) {
      const creator = creatorInfo;
      
      // Basic Info
      setCreatorName(creator.display_name);
      setCreatorId(creator.creator);
      setCreatorRegion(creator.region);
      setTierLevel(creator.tier_level || "");
      setPromotionalBody(creator.promotional_body || "");

      // Contact Info
      setContactEmail(creator.email);
      setEmailSame(creator.email === creator.payout_email);
      setPaymentEmail(creator.payout_email || '');
      setPhoneNumber(creator.phone_number);

      // Demographics
      setRace(creator.ethnicity || '');
      setGender(creator.gender || '');
      setLocation(creator.geolocation || "");
      setPrimaryMarket(creator.primary_markets);
      setNotesContentStyle(creator.notes_content_style);
      setInvalid_geogenderrace(creator.invalid_geogenderrace || false);

      // Additional
      setAvgViews(creator.avg_views || "");
      setManager(creator.manager || "");
      setSupervisor(creator.superviser || "");
      setPfphref(creator.pfphref || "");
      setIsVendor(creator.is_vendor);
      if (creator.marketplace_permission !== undefined) {
        setMarketplacePermission(creator.marketplace_permission);
      }

      // Instagram - strip URL prefix
      const instagramUsername = creator.instagram_link?.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "") || "";
      setInstagramLink(instagramUsername);
      setInstagramFollowerCount(creator.instagram ? formatNumber(creator.instagram) : "");
      setInstagramBrandRate(creator.rate_ig_reelbrand?.toFixed(2) || "");
      setInstagramSongRate(creator.rate_ig_reelsound?.toFixed(2) || "");
      setInstagramFeedRate(creator.rate_ig_feedpost?.toFixed(2) || "");
      setInstagramStoryRate(creator.rate_ig_story?.toFixed(2) || "");

      // TikTok - strip URL prefix and @
      const tiktokUsername = creator.tiktok_link?.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, "") || "";
      setTikTokLink(tiktokUsername);
      setTikTokFollowerCount(creator.tiktok ? formatNumber(creator.tiktok) : "");
      setTikTokBrandRate(creator.rate_tt_brand?.toFixed(2) || "");
      setTikTokSongRate(creator.rate_tt_sound?.toFixed(2) || "");

      // YouTube - keep @ if present or strip URL
      let youtubeUsername = creator.youtube_link?.replace(/^https?:\/\/(www\.)?youtube\.com\//i, "") || "";
      setYoutubeLink(youtubeUsername);
      setYoutubeFollowerCount(creator.youtube ? formatNumber(creator.youtube) : "");
      setYoutubeIntegrationRate(creator.rate_yt_integ?.toFixed(2) || "");
      setYoutubeShortsRate(creator.rate_yt_short?.toFixed(2) || "");
      setYoutubeDedicatedRate(creator.rate_yt_dedicated?.toFixed(2) || "");

      // X - strip URL prefix
      const xUsername = creator.x_link?.replace(/^https?:\/\/(www\.)?(x|twitter)\.com\//i, "") || "";
      setXLink(xUsername);
      setXFollowerCount(creator.follow_x ? formatNumber(creator.follow_x.toString()) : "");
      setXFeedPost(creator.rate_x_feedpost?.toFixed(2) || "");
      setXRepost(creator.rate_x_repost?.toFixed(2) || "");

      // Kick - strip URL prefix
      const kickUsername = creator.kick_link?.replace(/^https?:\/\/(www\.)?kick\.com\//i, "") || "";
      setKickLink(kickUsername);
      setKickFollowerCount(creator.follow_kick ? formatNumber(creator.follow_kick.toString()) : "");
      setKickPlacement(creator.rate_kick_product?.toFixed(2) || "");

      // Twitch - strip URL prefix
      const twitchUsername = creator.twitch_link?.replace(/^https?:\/\/(www\.)?twitch\.tv\//i, "") || "";
      setTwitchLink(twitchUsername);
      setTwitchFollowerCount(creator.twitch ? formatNumber(creator.twitch) : "");
      setTwitchPlacement(creator.rate_twitch_product?.toFixed(2) || "");

      // Podcast - full URL
      setPodcastLink(creator.podcast_link || "");
      setPodcastFollowerCount(creator.follow_podcast ? formatNumber(creator.follow_podcast) : "");
      setPodcastPreroll(creator.rate_podcast_preroll?.toFixed(2) || "");
      setPodcastMidroll(creator.rate_podcast_midroll?.toFixed(2) || "");
      setPodcastPostroll(creator.rate_podcast_postroll?.toFixed(2) || "");
      setPodcastFull(creator.rate_podcast_full?.toFixed(2) || "");
    }
  }, [creatorInfo]);

  const handleEmailSameChange = (event) => {
    setEmailSame(event.target.checked);
    if (event.target.checked) {
      setPaymentEmail(contactEmail);
    } else {
      setPaymentEmail("");
    }
  };

  // Handle platform link changes with validation
  const handlePlatformLinkChange = (platform, value, setter) => {
    const sanitized = sanitizeUsername(value, platform);
    setter(sanitized);
    
    // Validate
    const validation = validateUsername(sanitized, platform);
    setPlatformValidation(prev => ({
      ...prev,
      [platform]: validation.valid ? null : validation.message
    }));
  };

  const handleUpdateBasicInfo = async () => {
    setSavingSection('basic');
    try {
      const payload = {
        display_name: creatorName,
        region: creatorRegion,
        tier_level: tierLevel,
        promotional_body: promotionalBody && promotionalBody.trim().length > 0
          ? promotionalBody
          : "No current promotions available"
      };

      await client.creators.admin.updateBasic(creatorId, payload);
      
      toast.success('Basic information updated', {
        description: 'Changes have been saved'
      });
      
      queryClient.invalidateQueries(["creators"]);
    } catch (error) {
      toast.error('Error updating basic information', {
        description: error?.response?.data?.error || error.message
      });
      console.error("Error updating basic info", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleUpdateContactInfo = async () => {
    setSavingSection('contact');
    try {
      const payload = {
        email: contactEmail,
        payout_email: emailSame ? contactEmail : paymentEmail,
        phone_number: phoneNumber
      };

      await client.creators.admin.updateContact(creatorId, payload);
      
      toast.success('Contact information updated', {
        description: 'Changes have been saved'
      });
      
      queryClient.invalidateQueries(["creators"]);
    } catch (error) {
      toast.error('Error updating contact information', {
        description: error?.response?.data?.error || error.message
      });
      console.error("Error updating contact info", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleUpdatePlatforms = async () => {
    setSavingSection('platforms');
    
    // Validate all active platforms
    const errors = {};
    if (tikTokLink) {
      const validation = validateUsername(tikTokLink, 'TikTok');
      if (!validation.valid) errors.TikTok = validation.message;
    }
    if (instagramLink) {
      const validation = validateUsername(instagramLink, 'Instagram');
      if (!validation.valid) errors.Instagram = validation.message;
    }
    if (youtubeLink) {
      const validation = validateUsername(youtubeLink, 'YouTube');
      if (!validation.valid) errors.YouTube = validation.message;
    }
    if (xLink) {
      const validation = validateUsername(xLink, 'X');
      if (!validation.valid) errors.X = validation.message;
    }
    if (kickLink) {
      const validation = validateUsername(kickLink, 'Kick');
      if (!validation.valid) errors.Kick = validation.message;
    }
    if (twitchLink) {
      const validation = validateUsername(twitchLink, 'Twitch');
      if (!validation.valid) errors.Twitch = validation.message;
    }
    if (podcastLink) {
      const validation = validateUsername(podcastLink, 'Podcast');
      if (!validation.valid) errors.Podcast = validation.message;
    }
    
    if (Object.keys(errors).length > 0) {
      setPlatformValidation(errors);
      toast.error('Platform validation errors', {
        description: 'Please fix the errors before saving'
      });
      setSavingSection(null);
      return;
    }
    
    try {
      const formattedTikTokLink = tikTokLink ? `${PLATFORM_CONFIGS.TikTok.urlBase}${tikTokLink}` : null;
      const formattedInstagramLink = instagramLink ? `${PLATFORM_CONFIGS.Instagram.urlBase}${instagramLink}` : null;
      const formattedYouTubeLink = youtubeLink ? `${PLATFORM_CONFIGS.YouTube.urlBase}${youtubeLink}` : null;
      const formattedXLink = xLink ? `${PLATFORM_CONFIGS.X.urlBase}${xLink}` : null;
      const formattedKickLink = kickLink ? `${PLATFORM_CONFIGS.Kick.urlBase}${kickLink}` : null;
      const formattedTwitchLink = twitchLink ? `${PLATFORM_CONFIGS.Twitch.urlBase}${twitchLink}` : null;

      const payload = {
        tiktok: parseLocalInt(tikTokFollowerCount) || null,
        tiktok_link: formattedTikTokLink || null,
        instagram: parseLocalInt(instagramFollowerCount) || null,
        instagram_link: formattedInstagramLink || null,
        youtube: parseLocalInt(youtubeFollowerCount) || null,
        youtube_link: formattedYouTubeLink || null,
        twitch: parseLocalInt(twitchFollowerCount) || null,
        twitch_link: formattedTwitchLink || null,
        follow_x: parseLocalInt(xFollowerCount) || null,
        x_link: formattedXLink || null,
        follow_podcast: parseLocalInt(podcastFollowerCount) || null,
        podcast_link: podcastLink || null,
        follow_kick: parseLocalInt(kickFollowerCount) || null,
        kick_link: formattedKickLink || null,
        rate_tt_sound: parseLocalFloat(tikTokSongRate),
        rate_tt_brand: parseLocalFloat(tikTokBrandRate),
        rate_ig_feedpost: parseLocalFloat(instagramFeedRate),
        rate_ig_reelsound: parseLocalFloat(instagramSongRate),
        rate_ig_reelbrand: parseLocalFloat(instagramBrandRate),
        rate_ig_story: parseLocalFloat(instagramStoryRate),
        rate_yt_integ: parseLocalFloat(youtubeIntegrationRate),
        rate_yt_short: parseLocalFloat(youtubeShortsRate),
        rate_yt_dedicated: parseLocalFloat(youtubeDedicatedRate),
        rate_podcast_preroll: parseLocalFloat(podcastPreroll),
        rate_podcast_midroll: parseLocalFloat(podcastMidroll),
        rate_podcast_postroll: parseLocalFloat(podcastPostroll),
        rate_podcast_full: parseLocalFloat(podcastFull),
        rate_twitch_product: parseLocalFloat(twitchPlacement),
        rate_kick_product: parseLocalFloat(kickPlacement),
        rate_x_feedpost: parseLocalFloat(xFeedPost),
        rate_x_repost: parseLocalFloat(xRepost),
      };

      await client.creators.admin.updatePlatforms(creatorId, payload);
      
      toast.success('Platform information updated', {
        description: 'Changes have been saved'
      });
      
      queryClient.invalidateQueries(["creators"]);
      setPlatformValidation({});
    } catch (error) {
      toast.error('Error updating platform information', {
        description: error?.response?.data?.error || error.message
      });
      console.error("Error updating platforms", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleUpdateDemographics = async () => {
    setSavingSection('demographics');
    try {
      const payload = {
        geolocation: location,
        gender: gender,
        ethnicity: race,
        primary_markets: primaryMarket,
        notes_content_style: notesContentStyle,
        invalid_geogenderrace: invalid_geogenderrace
      };

      await client.creators.admin.updateDemographics(creatorId, payload);
      
      toast.success('Demographics updated', {
        description: 'Changes have been saved'
      });
      
      queryClient.invalidateQueries(["creators"]);
    } catch (error) {
      toast.error('Error updating demographics', {
        description: error?.response?.data?.error || error.message
      });
      console.error("Error updating demographics", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleUpdateAdditional = async () => {
    setSavingSection('additional');
    try {
      const payload = {
        pfphref: pfphref,
        avg_views: avgViews,
        manager: manager,
        superviser: supervisor,
        is_vendor: isVendor,
        marketplace_permission: marketplacePermission
      };

      await client.creators.admin.updateAdditional(creatorId, payload);
      
      toast.success('Additional information updated', {
        description: 'Changes have been saved'
      });
      
      queryClient.invalidateQueries(["creators"]);
    } catch (error) {
      toast.error('Error updating additional information', {
        description: error?.response?.data?.error || error.message
      });
      console.error("Error updating additional info", error);
    } finally {
      setSavingSection(null);
    }
  };

  // Calculate active platforms count
  const activePlatformsCount = [
    instagramLink,
    tikTokLink,
    youtubeLink,
    xLink,
    podcastLink,
    kickLink,
    twitchLink
  ].filter(Boolean).length;

  // Helper function to get input props for platform fields
  const getPlatformInputProps = (platform, value) => {
    const config = PLATFORM_CONFIGS[platform];
    const showPrefix = !config.isUrl && platform !== 'YouTube';
    const hasError = platformValidation[platform];
    const hasValue = value && value.trim() !== '';
    
    return {
      startAdornment: showPrefix ? (
        <InputAdornment position="start">
          <Typography sx={{ color: '#666', fontSize: '0.9rem' }}>
            {config.prefix}
          </Typography>
        </InputAdornment>
      ) : null,
      endAdornment: hasValue ? (
        <InputAdornment position="end">
          {hasError ? (
            <ErrorOutlineIcon sx={{ color: '#f44336' }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />
          )}
        </InputAdornment>
      ) : null,
    };
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '95vh',
          marginBottom: {
            xs: 'calc(86px + env(safe-area-inset-bottom, 0px))',
            md: 0
          },
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            width: 56,
            height: 56,
          }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              Creator Profile
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {mode === 'edit' ? 'Update creator information by section' : 'Add new creator profile'}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 60,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500
            }
          }}
        >
          <Tab label="Basic Info" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Contact" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="Platforms" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label="Demographics" icon={<LocationOnIcon />} iconPosition="start" />
          <Tab label="Additional" icon={<WorkIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        {/* Tab 0: Basic Information */}
        <TabPanel value={currentTab} index={0}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <PersonIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Basic Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Creator Name"
                    value={creatorName}
                    onChange={(e) => setCreatorName(e.target.value)}
                    variant="outlined"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Creator Region</InputLabel>
                    <Select
                      label="Creator Region"
                      value={creatorRegion}
                      onChange={(e) => setCreatorRegion(e.target.value)}
                    >
                      <MenuItem value="NACAUKAUS">USA, Canada, UK, Australia</MenuItem>
                      <MenuItem value="Europe">Europe</MenuItem>
                      <MenuItem value="Asia">Asia</MenuItem>
                      <MenuItem value="LATAM">LATAM</MenuItem>
                      <MenuItem value="Africa">Africa</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Tier Level</InputLabel>
                    <Select
                      label="Tier Level"
                      value={tierLevel}
                      onChange={(e) => setTierLevel(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <StarIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="celebrity">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon sx={{ color: '#FFD700' }} />
                          <span>Celebrity</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="macro">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon sx={{ color: '#C0C0C0' }} />
                          <span>Macro</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="micro">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon sx={{ color: '#CD7F32' }} />
                          <span>Micro</span>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Promotional Text"
                    multiline
                    rows={3}
                    variant="outlined"
                    value={promotionalBody}
                    onChange={(e) => setPromotionalBody(e.target.value)}
                    placeholder="Enter promotional details (Default: No current promotions available)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={savingSection === 'basic' ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdateBasicInfo}
                  disabled={savingSection === 'basic'}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {savingSection === 'basic' ? 'Saving...' : 'Save Basic Info'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 1: Contact Information */}
        <TabPanel value={currentTab} index={1}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <EmailIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Contact Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    variant="outlined"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PayPal Payment Email"
                    type="email"
                    disabled={emailSame}
                    value={paymentEmail}
                    onChange={(e) => setPaymentEmail(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={emailSame} 
                        onChange={handleEmailSameChange}
                        sx={{
                          color: '#667eea',
                          '&.Mui-checked': {
                            color: '#667eea',
                          },
                        }}
                      />
                    }
                    label="Payment and contact email are the same"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                    <MuiPhoneInput
                      country={"us"}
                      preferredCountries={["us"]}
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      isValid={(value) => value.length >= 10}
                      required
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={savingSection === 'contact' ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdateContactInfo}
                  disabled={savingSection === 'contact'}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {savingSection === 'contact' ? 'Saving...' : 'Save Contact Info'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 2: Platform Details */}
        <TabPanel value={currentTab} index={2}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <CategoryIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Platform Details
                </Typography>
                <Chip 
                  label={`${activePlatformsCount} Active`} 
                  size="small"
                  sx={{ 
                    bgcolor: '#667eea20',
                    color: '#667eea',
                    fontWeight: 600
                  }}
                />
              </Box>
              <Divider sx={{ mb: 3 }} />

              {Object.keys(platformValidation).length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Platform Validation Errors</AlertTitle>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {Object.entries(platformValidation).map(([platform, error]) => 
                      error && <li key={platform}><strong>{platform}:</strong> {error}</li>
                    )}
                  </ul>
                </Alert>
              )}

              {/* TikTok */}
              <Accordion 
                expanded={expandedPlatform === 'tiktok'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'tiktok' ? false : 'tiktok')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.TikTok.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>TikTok</Typography>
                    {tikTokLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="TikTok Username"
                        value={tikTokLink}
                        onChange={(e) => handlePlatformLinkChange('TikTok', e.target.value, setTikTokLink)}
                        error={!!platformValidation.TikTok}
                        helperText={platformValidation.TikTok || "Enter username without @"}
                        InputProps={getPlatformInputProps('TikTok', tikTokLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={tikTokFollowerCount}
                        onChange={(e) => setTikTokFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Brand Rate ($)"
                        value={tikTokBrandRate}
                        onChange={(e) => setTikTokBrandRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Sound Rate ($)"
                        value={tikTokSongRate}
                        onChange={(e) => setTikTokSongRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Instagram */}
              <Accordion 
                expanded={expandedPlatform === 'instagram'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'instagram' ? false : 'instagram')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.Instagram.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>Instagram</Typography>
                    {instagramLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Instagram Username"
                        value={instagramLink}
                        onChange={(e) => handlePlatformLinkChange('Instagram', e.target.value, setInstagramLink)}
                        error={!!platformValidation.Instagram}
                        helperText={platformValidation.Instagram || "Enter username without @"}
                        InputProps={getPlatformInputProps('Instagram', instagramLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={instagramFollowerCount}
                        onChange={(e) => setInstagramFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Brand Reel Rate ($)"
                        value={instagramBrandRate}
                        onChange={(e) => setInstagramBrandRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Song Reel Rate ($)"
                        value={instagramSongRate}
                        onChange={(e) => setInstagramSongRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Feed Post Rate ($)"
                        value={instagramFeedRate}
                        onChange={(e) => setInstagramFeedRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Story Rate ($)"
                        value={instagramStoryRate}
                        onChange={(e) => setInstagramStoryRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* YouTube */}
              <Accordion 
                expanded={expandedPlatform === 'youtube'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'youtube' ? false : 'youtube')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.YouTube.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>YouTube</Typography>
                    {youtubeLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="YouTube Handle"
                        value={youtubeLink}
                        onChange={(e) => handlePlatformLinkChange('YouTube', e.target.value, setYoutubeLink)}
                        error={!!platformValidation.YouTube}
                        helperText={platformValidation.YouTube || "Must start with @ (e.g., @yourchannel)"}
                        placeholder="@yourchannel"
                        InputProps={getPlatformInputProps('YouTube', youtubeLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Subscriber Count"
                        value={youtubeFollowerCount}
                        onChange={(e) => setYoutubeFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Integration Rate ($)"
                        value={youtubeIntegrationRate}
                        onChange={(e) => setYoutubeIntegrationRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shorts Rate ($)"
                        value={youtubeShortsRate}
                        onChange={(e) => setYoutubeShortsRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Dedicated Video Rate ($)"
                        value={youtubeDedicatedRate}
                        onChange={(e) => setYoutubeDedicatedRate(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* X (Twitter) */}
              <Accordion 
                expanded={expandedPlatform === 'x'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'x' ? false : 'x')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.X.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>X (Twitter)</Typography>
                    {xLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="X Username"
                        value={xLink}
                        onChange={(e) => handlePlatformLinkChange('X', e.target.value, setXLink)}
                        error={!!platformValidation.X}
                        helperText={platformValidation.X || "Enter username without @"}
                        InputProps={getPlatformInputProps('X', xLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={xFollowerCount}
                        onChange={(e) => setXFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Feed Post Rate ($)"
                        value={xFeedPost}
                        onChange={(e) => setXFeedPost(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Repost Rate ($)"
                        value={xRepost}
                        onChange={(e) => setXRepost(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Podcast */}
              <Accordion 
                expanded={expandedPlatform === 'podcast'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'podcast' ? false : 'podcast')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.Podcast.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>Podcast</Typography>
                    {podcastLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Podcast URL"
                        value={podcastLink}
                        onChange={(e) => handlePlatformLinkChange('Podcast', e.target.value, setPodcastLink)}
                        error={!!platformValidation.Podcast}
                        helperText={platformValidation.Podcast || "Enter your full podcast URL"}
                        InputProps={getPlatformInputProps('Podcast', podcastLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Listener Count"
                        value={podcastFollowerCount}
                        onChange={(e) => setPodcastFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Preroll Ad Rate ($)"
                        value={podcastPreroll}
                        onChange={(e) => setPodcastPreroll(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Midroll Ad Rate ($)"
                        value={podcastMidroll}
                        onChange={(e) => setPodcastMidroll(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Postroll Ad Rate ($)"
                        value={podcastPostroll}
                        onChange={(e) => setPodcastPostroll(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Full Episode Rate ($)"
                        value={podcastFull}
                        onChange={(e) => setPodcastFull(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Kick */}
              <Accordion 
                expanded={expandedPlatform === 'kick'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'kick' ? false : 'kick')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.Kick.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>Kick</Typography>
                    {kickLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Kick Username"
                        value={kickLink}
                        onChange={(e) => handlePlatformLinkChange('Kick', e.target.value, setKickLink)}
                        error={!!platformValidation.Kick}
                        helperText={platformValidation.Kick || "Enter username"}
                        InputProps={getPlatformInputProps('Kick', kickLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={kickFollowerCount}
                        onChange={(e) => setKickFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Product Placement Rate ($)"
                        value={kickPlacement}
                        onChange={(e) => setKickPlacement(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Twitch */}
              <Accordion 
                expanded={expandedPlatform === 'twitch'} 
                onChange={() => setExpandedPlatform(expandedPlatform === 'twitch' ? false : 'twitch')}
                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{PLATFORM_CONFIGS.Twitch.emoji}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>Twitch</Typography>
                    {twitchLink && (
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 'auto', mr: 2 }} 
                      />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Twitch Username"
                        value={twitchLink}
                        onChange={(e) => handlePlatformLinkChange('Twitch', e.target.value, setTwitchLink)}
                        error={!!platformValidation.Twitch}
                        helperText={platformValidation.Twitch || "Enter username"}
                        InputProps={getPlatformInputProps('Twitch', twitchLink)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={twitchFollowerCount}
                        onChange={(e) => setTwitchFollowerCount(formatNumber(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Product Placement Rate ($)"
                        value={twitchPlacement}
                        onChange={(e) => setTwitchPlacement(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={savingSection === 'platforms' ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdatePlatforms}
                  disabled={savingSection === 'platforms'}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {savingSection === 'platforms' ? 'Saving...' : 'Save Platforms'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 3: Demographics */}
        <TabPanel value={currentTab} index={3}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <LocationOnIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Demographics & Markets
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              {invalid_geogenderrace && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Invalid Geographic Data</Typography>
                  Please update the demographic information below
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={invalid_geogenderrace} 
                        onChange={(e) => setInvalid_geogenderrace(e.target.checked)}
                        sx={{
                          color: '#f57c00',
                          '&.Mui-checked': {
                            color: '#f57c00',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningAmberIcon fontSize="small" sx={{ color: '#f57c00' }} />
                        <span>Invalid GeoData? (Uncheck this after fixing)</span>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Non-binary">Non-binary</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Race (optional)</InputLabel>
                    <Select
                      label="Race (optional)"
                      value={race}
                      onChange={(e) => setRace(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="Asian">Asian</MenuItem>
                      <MenuItem value="Black">Black</MenuItem>
                      <MenuItem value="Hispanic">Hispanic</MenuItem>
                      <MenuItem value="White">White</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Primary Markets</InputLabel>
                    <Select
                      multiple
                      label="Primary Markets"
                      value={primaryMarket}
                      onChange={(e) => setPrimaryMarket(e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {niches.map((market) => (
                        <MenuItem key={market} value={market}>
                          {market}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bio / Content Style Notes"
                    multiline
                    rows={4}
                    placeholder="Tell us about your content style and audience"
                    variant="outlined"
                    value={notesContentStyle}
                    onChange={(e) => setNotesContentStyle(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={savingSection === 'demographics' ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdateDemographics}
                  disabled={savingSection === 'demographics'}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {savingSection === 'demographics' ? 'Saving...' : 'Save Demographics'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 4: Additional Information */}
        <TabPanel value={currentTab} index={4}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <WorkIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Additional Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Profile Photo URL"
                    value={pfphref}
                    onChange={(e) => setPfphref(e.target.value)}
                    variant="outlined"
                    placeholder="Enter URL for profile photo"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ImageIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Average Views"
                    value={avgViews}
                    onChange={(e) => setAvgViews(e.target.value)}
                    variant="outlined"
                    placeholder="Average views per post"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Manager"
                    value={manager}
                    onChange={(e) => setManager(e.target.value)}
                    variant="outlined"
                    placeholder="Manager name"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Supervisor"
                    value={supervisor}
                    onChange={(e) => setSupervisor(e.target.value)}
                    variant="outlined"
                    placeholder="Supervisor ID"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Settings" size="small" />
                  </Divider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={isVendor} 
                        onChange={(e) => setIsVendor(e.target.checked)}
                        sx={{
                          color: '#667eea',
                          '&.Mui-checked': {
                            color: '#667eea',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkIcon fontSize="small" />
                        <span>Is Vendor?</span>
                      </Box>
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={marketplacePermission} 
                        onChange={(e) => setMarketplacePermission(e.target.checked)}
                        sx={{
                          color: '#667eea',
                          '&.Mui-checked': {
                            color: '#667eea',
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VerifiedIcon fontSize="small" />
                        <span>Marketplace Permission</span>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={savingSection === 'additional' ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleUpdateAdditional}
                  disabled={savingSection === 'additional'}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {savingSection === 'additional' ? 'Saving...' : 'Save Additional Info'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default CreatorDialog;